import { useQuery } from "@tanstack/react-query";
import type { ReactElement } from "react";

import { apiClient } from "../api/client";

/**
 * Cycle 15 STORY-1504 — Public uptime page (no auth).
 *
 * Минимальный subset для клиентов: только status (operational/degraded/outage/unknown).
 * Used by sales: "у нас прозрачный uptime page". Used by clients: "проверить status перед звонком в support".
 */

type PublicProductStatus = {
  name: string;
  status: "operational" | "degraded" | "outage" | "unknown";
  last_checked_age_seconds: number | null;
};

type PublicUptimeResponse = {
  timestamp: string;
  overall: "operational" | "degraded" | "outage" | "unknown";
  products: PublicProductStatus[];
};

const STATUS_TEXT: Record<PublicProductStatus["status"], { ru: string; color: string; bg: string }> = {
  operational: { ru: "Работает", color: "#0a7a4a", bg: "#e6f7ee" },
  degraded: { ru: "Замедленно", color: "#a07000", bg: "#fff7d6" },
  outage: { ru: "Недоступен", color: "#a01c2a", bg: "#fde8eb" },
  unknown: { ru: "Нет данных", color: "#5a6378", bg: "#eef0f5" },
};

const OVERALL_BANNER: Record<PublicUptimeResponse["overall"], { ru: string; color: string; bg: string }> = {
  operational: { ru: "Все системы работают штатно", color: "#0a7a4a", bg: "#d6f5e3" },
  degraded: { ru: "Частичная деградация некоторых систем", color: "#a07000", bg: "#fff0c2" },
  outage: { ru: "Зафиксированы критические проблемы", color: "#a01c2a", bg: "#fed5da" },
  unknown: { ru: "Статус не определён", color: "#5a6378", bg: "#eef0f5" },
};

function formatLastCheck(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 60) return `${seconds} сек назад`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} мин назад`;
  return `${Math.floor(seconds / 3600)} ч назад`;
}

export function PublicUptimePage(): ReactElement {
  const { data, isLoading, error } = useQuery<PublicUptimeResponse>({
    queryKey: ["public-uptime"],
    queryFn: async () => {
      const response = await apiClient.get<PublicUptimeResponse>("/api/portfolio/uptime");
      return response.data;
    },
    refetchInterval: 60_000,
  });

  if (isLoading) {
    return (
      <main style={{ padding: 48, maxWidth: 720, margin: "0 auto" }}>
        <h1>Uptime</h1>
        <p>Загружаем статус...</p>
      </main>
    );
  }

  if (error || !data) {
    return (
      <main style={{ padding: 48, maxWidth: 720, margin: "0 auto" }}>
        <h1>Uptime</h1>
        <p>Не удалось загрузить статус. Попробуйте обновить страницу.</p>
      </main>
    );
  }

  const banner = OVERALL_BANNER[data.overall];

  return (
    <main style={{ padding: "48px 24px", maxWidth: 800, margin: "0 auto" }}>
      <header style={{ marginBottom: 32 }}>
        <h1 style={{ margin: "0 0 8px", fontSize: 28 }}>IT-Navigate · Uptime</h1>
        <p style={{ margin: 0, color: "#5a6378", fontSize: 14 }}>
          Текущий статус всех продуктов IT-Navigate. Обновляется автоматически каждую минуту.
        </p>
      </header>

      <div
        role="status"
        style={{
          padding: "20px 24px",
          background: banner.bg,
          color: banner.color,
          borderRadius: 12,
          marginBottom: 32,
          fontSize: 18,
          fontWeight: 600,
          textAlign: "center",
        }}
      >
        {banner.ru}
      </div>

      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {data.products.map((p) => {
          const cfg = STATUS_TEXT[p.status];
          return (
            <li
              key={p.name}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "14px 18px",
                background: "white",
                border: "1px solid #e1e6f0",
                borderRadius: 8,
                marginBottom: 8,
              }}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 12, color: "#8693b0", marginTop: 2 }}>
                  Проверка: {formatLastCheck(p.last_checked_age_seconds)}
                </div>
              </div>
              <span
                style={{
                  padding: "5px 14px",
                  background: cfg.bg,
                  color: cfg.color,
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                }}
              >
                {cfg.ru}
              </span>
            </li>
          );
        })}
      </ul>

      <footer style={{ marginTop: 48, fontSize: 12, color: "#8693b0", textAlign: "center" }}>
        <p style={{ margin: 0 }}>
          Источник: synthetic monitoring · Раскрытие SLO: <a href="https://portal.it-navigator.online/api/sso/public-config">/api/sso/public-config</a>
        </p>
        <p style={{ margin: "8px 0 0" }}>Last update: {new Date(data.timestamp).toLocaleString("ru")}</p>
      </footer>
    </main>
  );
}
